/**
 * Slide-out ticket submission widget for chefs.  Mounted globally in App.js
 * via the floating action button (FAB) and accessible from any authenticated
 * page.  Uses Shadcn Sheet for the panel.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageSquarePlus, Send, Loader2, Star, X, CheckCircle2,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import { friendlyError } from "@/lib/friendlyError";

const CATEGORIES = [
  { k: "bug_or_complaint",  label: "Bug / Complaint",     emoji: "🐛" },
  { k: "feature_request",   label: "Feature Request",     emoji: "💡" },
  { k: "billing_issue",     label: "Billing Issue",       emoji: "💳" },
  { k: "translation_issue", label: "Translation Issue",   emoji: "🌐" },
  { k: "general_feedback",  label: "General Feedback",    emoji: "💬" },
];

export default function TicketSubmitWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);  // {reference} on success

  const reset = () => {
    setCategory(""); setSubject(""); setMessage(""); setRating(0);
    setSubmitted(null); setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error("Pick a category"); return; }
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await axios.post(
        `${API}/tickets`,
        {
          subject: subject.trim(), message: message.trim(),
          category, rating: rating || null,
          metadata: { page: window.location.pathname },
        },
        { headers: { Authorization: `Bearer ${session?.access_token || ""}` } }
      );
      setSubmitted({ reference: res.data.reference });
      toast.success(`Ticket created: ${res.data.reference}`);
    } catch (err) {
      toast.error(friendlyError(err, "Couldn't submit feedback. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="fixed bottom-24 right-6 z-[60] inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 px-4 py-3 text-slate-950 font-bold shadow-lg shadow-amber-500/30 transition-transform hover:scale-105"
          data-testid="ticket-fab"
        >
          <MessageSquarePlus className="h-4 w-4" />
          <span className="hidden sm:inline">Feedback</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md bg-slate-950 border-l border-amber-500/20 overflow-y-auto" data-testid="ticket-submit-sheet">
        <SheetHeader>
          <SheetTitle className="text-white">
            {submitted ? "Ticket received ✨" : "Share feedback or report an issue"}
          </SheetTitle>
          <SheetDescription className="text-slate-400 text-xs">
            {submitted
              ? "Our team will get back to you in the app shortly."
              : "Direct line to the PlateProfit team. We read everything."}
          </SheetDescription>
        </SheetHeader>

        {submitted ? (
          <div className="mt-8 space-y-4 text-center" data-testid="ticket-submit-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <div>
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono">Reference</div>
              <div className="text-2xl font-bold text-amber-400 font-mono mt-1" data-testid="ticket-submit-reference">
                {submitted.reference}
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed px-4">
              Bookmark this reference if you need to follow up. You&apos;ll get an in-app
              notification when we reply.
            </p>
            <Button
              onClick={() => setOpen(false)}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              data-testid="ticket-submit-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.18em] text-slate-500 font-mono mb-2">Category</label>
              <div className="grid grid-cols-1 gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.k} type="button"
                    onClick={() => setCategory(c.k)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded border text-left text-xs font-semibold transition-colors",
                      category === c.k
                        ? "bg-amber-500/15 text-amber-300 border-amber-400"
                        : "bg-slate-900 text-slate-400 border-slate-800 hover:border-slate-700"
                    )}
                    data-testid={`ticket-category-${c.k}`}
                  >
                    <span className="text-base">{c.emoji}</span>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.18em] text-slate-500 font-mono mb-2">Subject</label>
              <Input
                value={subject} onChange={(e) => setSubject(e.target.value)}
                required minLength={2} maxLength={180}
                placeholder="One-line summary"
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="ticket-subject"
              />
            </div>

            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.18em] text-slate-500 font-mono mb-2">Message</label>
              <Textarea
                value={message} onChange={(e) => setMessage(e.target.value)}
                required minLength={2} maxLength={5000}
                rows={5}
                placeholder="Tell us what happened or what you'd love to see…"
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="ticket-message"
              />
            </div>

            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.18em] text-slate-500 font-mono mb-2">Rating (optional)</label>
              <div className="flex items-center gap-1" data-testid="ticket-rating">
                {[1,2,3,4,5].map((n) => (
                  <button
                    key={n} type="button"
                    onClick={() => setRating(rating === n ? 0 : n)}
                    className="p-1 rounded hover:bg-slate-800"
                    data-testid={`ticket-rating-${n}`}
                    aria-label={`${n} star${n>1?"s":""}`}
                  >
                    <Star className={cn("h-5 w-5", n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-600")} />
                  </button>
                ))}
                {rating > 0 && (
                  <button type="button" onClick={() => setRating(0)} className="ml-2 text-[0.65rem] text-slate-500 hover:text-slate-300 inline-flex items-center gap-1">
                    <X className="h-3 w-3" />clear
                  </button>
                )}
              </div>
            </div>

            <div className="pt-2 flex items-center justify-between">
              <button type="button" onClick={() => setOpen(false)}
                      className="text-xs text-slate-500 hover:text-slate-300"
                      data-testid="ticket-cancel">Cancel</button>
              <Button type="submit" disabled={busy || !category || !subject || !message}
                      className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold disabled:opacity-40"
                      data-testid="ticket-submit">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Submit
              </Button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
